import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

/**
 * AI アシスタント soremon の API（BE `soremon/api_views.py`）。
 *
 * soremon は SOREMO MCP と同じツールをチャットから呼び出す。確認・検索（low）と
 * 作成・更新（medium）はそのまま実行され、`tools` に残る。削除・送信・共有・契約
 * などの high リスクの操作は BE が確認待ち（action.status = "pending"）で止めるので、
 * FE が「実行する / キャンセル」を送ってから続きが進む。
 * 会話を返す API はすべて同じ `SoremonConversation` を返す。
 */

export type SoremonActionStatus = "pending" | "executed" | "cancelled";

export interface SoremonAction {
  id: number;
  tool_name: string;
  /** 画面に出す操作名（例: "タスクを作成"） */
  label: string;
  arguments: Record<string, unknown>;
  status: SoremonActionStatus;
}

export interface SoremonToolUse {
  name: string;
  label: string;
  /** low = 確認・検索（自動）/ medium = 作成・更新（確認なしで実行済み） */
  risk: "low" | "medium";
}

/** user の発言に添付したファイル（MessageItem の MessageFile として表示できる形）。 */
export interface SoremonFile {
  id: string;
  file_name: string;
  file_type: "image" | "document" | "audio" | "other";
  original_url: string;
  download_url: string | null;
  size: number;
  /** 内容の読み取りに失敗した理由（空なら成功） */
  error: string;
}

/** 送信時の添付（purpose=soremon_attachment でアップロード済みのもの）。 */
export interface SoremonAttachmentInput {
  file_key: string;
  file_name: string;
}

export interface SoremonMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  created: string;
  /** user のみ: 添付ファイル */
  files?: SoremonFile[];
  /** assistant のみ: 確認なしで実行したツール（low = 確認・検索、medium = 作成・更新） */
  tools?: SoremonToolUse[];
  /** assistant のみ: 確認が必要な（high リスクの）ツール */
  actions?: SoremonAction[];
}

export interface SoremonConversation {
  conversation_id: number;
  messages: SoremonMessage[];
  has_pending_action: boolean;
}

interface SoremonResponse<T> {
  status: string;
  data: T;
}

export const soremonApi = createApi({
  reducerPath: "soremonApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  endpoints: (builder) => {
    // 会話を返す mutation は、結果で会話キャッシュを置き換える。
    const conversationMutation = <Arg>(
      query: (arg: Arg) => { url: string; method: "POST"; body?: unknown },
    ) =>
      builder.mutation<SoremonConversation, Arg>({
        query,
        transformResponse: (res: SoremonResponse<SoremonConversation>) => res.data,
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            // upsertQueryData は値にも query 側の transformResponse を通すため使えない。
            dispatch(
              soremonApi.util.updateQueryData("getSoremonConversation", undefined, () => data),
            );
          } catch {
            // エラー表示は呼び出し側で行う
          }
        },
      });

    return {
      getSoremonStatus: builder.query<{ available: boolean }, void>({
        query: () => "/api/soremon/status",
        transformResponse: (res: SoremonResponse<{ available: boolean }>) => res.data,
      }),
      getSoremonConversation: builder.query<SoremonConversation, void>({
        query: () => "/api/soremon/conversation",
        transformResponse: (res: SoremonResponse<SoremonConversation>) => res.data,
      }),
      sendSoremonMessage: conversationMutation<{
        text: string;
        attachments?: SoremonAttachmentInput[];
      }>(({ text, attachments }) => ({
        url: "/api/soremon/messages",
        method: "POST",
        body: { text, attachments: attachments ?? [] },
      })),
      confirmSoremonAction: conversationMutation<number>((id) => ({
        url: `/api/soremon/actions/${id}/confirm`,
        method: "POST",
      })),
      cancelSoremonAction: conversationMutation<number>((id) => ({
        url: `/api/soremon/actions/${id}/cancel`,
        method: "POST",
      })),
      resetSoremonConversation: conversationMutation<void>(() => ({
        url: "/api/soremon/conversation/reset",
        method: "POST",
      })),
    };
  },
});

export const {
  useGetSoremonStatusQuery,
  useGetSoremonConversationQuery,
  useSendSoremonMessageMutation,
  useConfirmSoremonActionMutation,
  useCancelSoremonActionMutation,
  useResetSoremonConversationMutation,
} = soremonApi;
